/**
 * About Page
 * Premium about page showcasing company story, values, and team
 * Content is fetched from Shopify metafields with fallback to defaults
 */

import { Layout } from '~/components/layout/Layout';
import { motion } from 'motion/react';
import {
  IconHeadset,
  IconArrowRight,
  IconShieldCheck,
  IconAward,
  IconUsers,
  IconShippingFast,
  IconStar,
  IconCheck,
  IconSpinner,
} from '~/components/ui/Icons';
import { aboutContent } from '~/lib/cms/content';
import { usePageContent } from '~/lib/cms/useContent';

export function AboutPage() {
  const { content: pageContent, loading } = usePageContent('about');

  // Merge Shopify content with defaults
  const content = {
    ...aboutContent,
    hero: {
      ...aboutContent.hero,
      title: pageContent.hero?.title || aboutContent.hero.title,
      subtitle: pageContent.hero?.subtitle || aboutContent.hero.subtitle,
      badge: pageContent.hero?.badge || aboutContent.hero.badge,
    },
    stats: pageContent.stats || aboutContent.stats,
    cta: {
      ...aboutContent.cta,
      title: pageContent.cta?.title || aboutContent.cta.title,
      subtitle: pageContent.cta?.subtitle || aboutContent.cta.subtitle,
      primaryButton: {
        text: pageContent.cta?.buttonText || aboutContent.cta.primaryButton.text,
        href: pageContent.cta?.buttonLink || aboutContent.cta.primaryButton.href,
      },
    },
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <IconSpinner size={40} className="text-brand-orange animate-spin" />
        </div>
      </Layout>
    );
  }

  // Icon mapping for values
  const valueIcons: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    quality: IconAward,
    expertise: IconHeadset,
    service: IconUsers,
    speed: IconShippingFast,
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-surface-coal via-surface-black to-surface-graphite" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,116,0,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(147,51,234,0.08),transparent_60%)]" />

        {/* Decorative Elements */}
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-brand-orange/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="container-wide relative py-24 lg:py-36">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange/10 border border-brand-orange/20 rounded-full mb-6">
                <span className="text-sm font-mono text-brand-orange">{content.hero.badge}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-6 leading-tight">
                {content.hero.title}
              </h1>

              <p className="text-xl text-accent-silver leading-relaxed mb-8">
                {content.hero.subtitle}
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/collections/all-products" className="btn-primary">
                  Shop Our Collection
                  <IconArrowRight size={18} />
                </a>
                <a href="/contact" className="btn-secondary">
                  Get in Touch
                </a>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {content.stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="group relative p-6 rounded-2xl bg-surface-coal/60 backdrop-blur-xl border border-surface-slate/30 hover:border-brand-orange/30 transition-all duration-300"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative">
                    <div className="text-4xl md:text-5xl font-display font-bold text-brand-orange mb-2">
                      {stat.value}
                    </div>
                    <div className="text-accent-white font-medium mb-1">{stat.label}</div>
                    <div className="text-sm text-accent-silver">{stat.description}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted Brands Bar */}
      <section className="border-y border-surface-slate/20 bg-surface-coal/30">
        <div className="container-wide py-8">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            <span className="text-xs font-mono uppercase tracking-widest text-accent-silver">
              We Stock
            </span>
            {['Jabra', 'Poly', 'Axtel'].map((brand) => (
              <span key={brand} className="text-2xl font-display font-bold text-accent-platinum/50">
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="container-wide py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm font-mono uppercase tracking-widest text-brand-orange mb-4 block">
              Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-accent-white mb-8">
              {content.story.title}
            </h2>

            <div className="space-y-6">
              {content.story.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-lg text-accent-silver leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            {/* About Image */}
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
              <img
                src="https://tr9ath-e6.myshopify.com/cdn/shop/files/call-center-smile-and-woman-in-office-consulting-2025-04-06-10-17-31-utc.jpg?v=1763047875&width=1200"
                alt="Professional using a headset in an office environment"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-surface-black via-surface-coal to-surface-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,116,0,0.05),transparent_60%)]" />

        <div className="container-wide relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-mono uppercase tracking-widest text-brand-orange mb-4 block">
              What Drives Us
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-accent-white mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-accent-silver max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.values.map((value, index) => {
              const Icon = valueIcons[value.icon] || IconCheck;

              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative p-8 rounded-2xl bg-surface-graphite/50 border border-surface-slate/30 hover:border-brand-orange/30 transition-all duration-300"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative">
                    <div className="w-14 h-14 rounded-xl bg-brand-orange/10 flex items-center justify-center mb-5 group-hover:bg-brand-orange/20 transition-colors">
                      <Icon size={28} className="text-brand-orange" />
                    </div>

                    <h3 className="text-xl font-semibold text-accent-white mb-3">
                      {value.title}
                    </h3>

                    <p className="text-accent-silver leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="container-wide py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1"
          >
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: IconShieldCheck, text: 'Genuine Products' },
                { icon: IconAward, text: '2-Year Warranty' },
                { icon: IconShippingFast, text: 'Free Next-Day' },
                { icon: IconUsers, text: 'Expert Support' },
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-surface-coal border border-surface-slate/30"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0">
                    <item.icon size={20} className="text-brand-orange" />
                  </div>
                  <span className="text-sm font-medium text-accent-white">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="order-1 lg:order-2"
          >
            <span className="text-sm font-mono uppercase tracking-widest text-brand-orange mb-4 block">
              Why IT Remarketing
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-accent-white mb-6">
              More than just a retailer
            </h2>
            <p className="text-lg text-accent-silver leading-relaxed mb-6">
              We understand that the right headset can transform your work day.
              From recommending the perfect product to supporting you after purchase, we're here
              to help.
            </p>

            <ul className="space-y-3">
              {[
                'Genuine products with full manufacturer warranty',
                'Knowledgeable team ready to help you choose',
                'Free setup guides and ongoing support',
                '30-day hassle-free returns',
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-accent-platinum">
                  <IconCheck size={18} className="text-brand-orange flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container-wide py-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl"
        >
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-surface-coal via-surface-graphite to-surface-coal" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,116,0,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(147,51,234,0.1),transparent_50%)]" />

          {/* Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

          <div className="relative p-12 md:p-20 text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-accent-white mb-4">
              {content.cta.title}
            </h2>
            <p className="text-xl text-accent-silver mb-10 max-w-xl mx-auto">
              {content.cta.subtitle}
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a href={content.cta.primaryButton.href} className="btn-primary px-8 py-4 text-lg">
                {content.cta.primaryButton.text}
                <IconArrowRight size={20} />
              </a>
              <a href={content.cta.secondaryButton.href} className="btn-secondary px-8 py-4 text-lg">
                {content.cta.secondaryButton.text}
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
}
